import React from 'react';
import moment from 'moment';
import { parseMentions } from 'utils/mentionUtils';
import InventoryTable from './SpaceMessageInventoryTable';
import SpaceTaskCard from './SpaceMessageTaskCard';
import SpaceTaskCreatingIndicator from './SpaceMessageTaskCreatingIndicator';

interface AIResponseRendererProps {
    content: string;
    messageType?: string;
    createdAt?: string;
}

interface PurchaseOrder {
    id: string;
    taId: string;
    vendor: {
        profile: {
            businessName: string;
        };
        firstName?: string;
        lastName?: string;
    };
    status: string;
    totalAmount: number;
    currency: string;
    itemsCount: number;
    issuedDate: string;
    dueDate: string;
}

interface StructuredResponse {
    success: boolean;
    message: string;
    purchaseOrders?: PurchaseOrder[];
    salesOrders?: any[];
    inventory?: any[];
    task?: {
        title: string;
        description: string;
        createdAt: string;
        assignedTo: {
            id: string;
            name: string;
            avatar?: string;
        };
    };
}

const SpaceAIResponseRenderer: React.FC<AIResponseRendererProps> = ({ content, messageType, createdAt }) => {
    

    // Clean up AI response content by removing processing messages
    const cleanContent = (rawContent: string): string => {
        if (!rawContent || typeof rawContent !== 'string') return rawContent;
        
        // Remove common processing messages that shouldn't appear in final responses
        return rawContent
            .replace(/^Processing your request\.?\s*/i, '') // Remove at start
            .replace(/Processing your request\.?\s*/gi, '') // Remove anywhere
            .replace(/^Processing\.{3}\s*/i, '') // Remove "Processing..." 
            .replace(/Processing\.{3}\s*/gi, '') // Remove anywhere
            .trim();
    };

    // Clean the content before processing
    const cleanedContent = cleanContent(content);
    
    // Handle special content types
    if (cleanedContent === "INVENTORY_TABLE") {
        // This should be handled via the structured JSON response with inventory data from API
        // If we reach here, it means the API didn't provide inventory data in the expected format
        return (
            <div className="space-y-6">
                <div className="text-sm text-gray-900 mb-4">
                    Here is all the inventory that was added to our California warehouse
                </div>
                <div>
                    <InventoryTable />
                </div>
            </div>
        );
    }

    if (cleanedContent === "CREATING_TASK") {
        return (
            <div>
                <SpaceTaskCreatingIndicator />
            </div>
        );
    }

    if (cleanedContent === "TASK_CREATED") {
        return (
            <div>
                <div className="text-sm text-gray-600 italic">Task created successfully</div>
            </div>
        );
    }
    
    // Try to parse as JSON first
    let structuredData: StructuredResponse | null = null;
    
    try {
        structuredData = JSON.parse(cleanedContent);
    } catch (e) {
        // If not JSON, treat as regular text
        return (
            <div>
                <div>{parseMentions(cleanedContent)}</div>
            </div>
        );
    }

    // If it's structured data, render it nicely
    if (structuredData && typeof structuredData === 'object') {
        return (
            <div className="space-y-3">
                {/* Main message */}
                {structuredData.message && (
                    <div className="text-sm text-gray-900">
                        {parseMentions(structuredData.message)}
                    </div>
                )}

                {/* Purchase Orders */}
                {structuredData.purchaseOrders && structuredData.purchaseOrders.length > 0 && (
                    <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-3">
                            Purchase Orders ({structuredData.purchaseOrders.length})
                        </div>
                        <div className="space-y-2">
                            {structuredData.purchaseOrders.slice(0, 5).map((po: PurchaseOrder) => (
                                <div 
                                    key={po.id} 
                                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="font-medium text-sm text-gray-900">
                                                #{po.taId}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                {po.vendor.profile?.businessName || 
                                                 `${po.vendor.firstName} ${po.vendor.lastName}`}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-medium text-sm text-gray-900">
                                                {po.currency} {po.totalAmount.toLocaleString()}
                                            </div>
                                            <div className="text-xs text-gray-600 capitalize">
                                                {po.status}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>{po.itemsCount} item{po.itemsCount !== 1 ? 's' : ''}</span>
                                        <span>Due: {new Date(po.dueDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            ))}
                            {structuredData.purchaseOrders.length > 5 && (
                                <div className="text-xs text-gray-500 text-center py-2">
                                    ... and {structuredData.purchaseOrders.length - 5} more
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Sales Orders*/}
                {structuredData.salesOrders && structuredData.salesOrders.length > 0 && (
                    <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-3">
                            Sales Orders ({structuredData.salesOrders.length})
                        </div>
                    </div>
                )}

                {structuredData.inventory && structuredData.inventory.length > 0 && (
                    <div className="mt-4">
                        <div className="text-sm text-gray-900 mb-4">
                            Here are the inventory results from the warehouse
                        </div>
                        <InventoryTable data={structuredData.inventory} />
                    </div>
                )}

                {/* Task Created */}
                {structuredData.task && (
                    <div className="mt-4">
                        <SpaceTaskCard {...structuredData.task} />
                    </div>
                )}
                
            </div>
        );
    }

    // Fallback to regular text rendering
    return (
        <div>
            <div>{parseMentions(content)}</div>
        </div>
    );
};

export default SpaceAIResponseRenderer;


